import { useEffect, useState, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Play,
  Music,
  HardDrive,
  TrendingUp,
  Users,
  Search,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Smartphone,
  Monitor,
  Clock,
  Star,
  Gem,
  Flame,
  MapPin,
  Sparkles,
  Wand2,
} from "lucide-react";
import type {
  AnalyticsOverviewResponse,
  CountryBreakdownResponse,
  RegionBreakdownResponse,
  DeviceBreakdownResponse,
  HourlyHeatmapResponse,
  DailyListenersResponse,
  FeaturedHistoryResponse,
  AiAnalyticsResponse,
} from "@wizard-sounds/shared";
import { api } from "@/lib/api";
import { cn, formatPlayCount } from "@/lib/utils";
import { useTimezone, getTimezoneOffsetHours, timezoneLabel, shiftHeatmap } from "@/lib/timezone";

function formatBytes(bytes: number): string {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return <span className="flex items-center gap-0.5 font-mono text-[10px] text-fog"><Minus className="h-3 w-3" />flat</span>;
  if (previous === 0) return <span className="flex items-center gap-0.5 font-mono text-[10px] text-moss"><ArrowUpRight className="h-3 w-3" />new</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return <span className="flex items-center gap-0.5 font-mono text-[10px] text-fog"><Minus className="h-3 w-3" />flat</span>;
  const up = pct > 0;
  return (
    <span className={cn("flex items-center gap-0.5 font-mono text-[10px]", up ? "text-moss" : "text-ember")}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {up ? "+" : ""}{pct}%
    </span>
  );
}

const tooltipStyle = {
  backgroundColor: "#11172a",
  border: "1px solid #252e47",
  borderRadius: "12px",
  fontSize: 12,
  fontFamily: "var(--font-mono)",
  color: "#f5f0e6",
};

const COUNTRY_NAMES: Record<string, string> = {
  US: "United States", GB: "United Kingdom", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", JP: "Japan", BR: "Brazil", IN: "India",
  MX: "Mexico", NL: "Netherlands", SE: "Sweden", NO: "Norway", DK: "Denmark",
  FI: "Finland", ES: "Spain", IT: "Italy", PT: "Portugal", PL: "Poland",
  KR: "South Korea", CN: "China", RU: "Russia", ZA: "South Africa",
  NZ: "New Zealand", IE: "Ireland", CH: "Switzerland", AT: "Austria",
  BE: "Belgium", AR: "Argentina", CL: "Chile", CO: "Colombia",
  SG: "Singapore", HK: "Hong Kong", TW: "Taiwan", PH: "Philippines",
  TH: "Thailand", MY: "Malaysia", ID: "Indonesia", VN: "Vietnam",
  TR: "Turkey", UA: "Ukraine", CZ: "Czech Republic", RO: "Romania",
  HU: "Hungary", GR: "Greece", IL: "Israel", AE: "UAE", SA: "Saudi Arabia",
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEVICE_COLORS = ["#ffb649", "#8b95a8"];
const PICK_ICONS: Record<string, React.ReactNode> = {
  daily: <Star className="h-3.5 w-3.5 text-spark" />,
  trending: <Flame className="h-3.5 w-3.5 text-ember" />,
  gem: <Gem className="h-3.5 w-3.5 text-fog" />,
};

const TIME_RANGES = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
] as const;

type RangeDays = (typeof TIME_RANGES)[number]["days"];

function RangeToggle({ value, onChange }: { value: RangeDays; onChange: (d: RangeDays) => void }) {
  return (
    <div className="flex items-center rounded-xl border border-mist bg-ink p-0.5">
      {TIME_RANGES.map((r) => (
        <button
          key={r.days}
          onClick={() => onChange(r.days)}
          className={cn(
            "rounded-lg px-3 py-1 font-mono text-[11px] font-medium transition-colors",
            value === r.days
              ? "bg-spark text-ink"
              : "text-fog hover:text-parchment",
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

export function AdminAnalytics() {
  const [days, setDays] = useState<RangeDays>(30);
  const [data, setData] = useState<AnalyticsOverviewResponse | null>(null);
  const [countries, setCountries] = useState<CountryBreakdownResponse | null>(null);
  const [regions, setRegions] = useState<RegionBreakdownResponse | null>(null);
  const [devices, setDevices] = useState<DeviceBreakdownResponse | null>(null);
  const [heatmap, setHeatmap] = useState<HourlyHeatmapResponse | null>(null);
  const [listeners, setListeners] = useState<DailyListenersResponse | null>(null);
  const [featured, setFeatured] = useState<FeaturedHistoryResponse | null>(null);
  const [aiStats, setAiStats] = useState<AiAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { tz, toggleUtc } = useTimezone();

  const fetchData = useCallback((d: number) => {
    setLoading(true);
    Promise.all([
      api.admin.analyticsOverview(d).then(setData).catch(() => {}),
      api.admin.analyticsCountries(d).then(setCountries).catch(() => {}),
      api.admin.analyticsRegions(undefined, d).then(setRegions).catch(() => {}),
      api.admin.analyticsDevices(d).then(setDevices).catch(() => {}),
      api.admin.analyticsHourly(d).then(setHeatmap).catch(() => {}),
      api.admin.analyticsListeners(d).then(setListeners).catch(() => {}),
      api.admin.analyticsFeaturedHistory().then(setFeatured).catch(() => {}),
      api.admin.analyticsAi(d).then(setAiStats).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  const handleRangeChange = useCallback((d: RangeDays) => { setDays(d); }, []);

  const rangeLabel = TIME_RANGES.find((r) => r.days === days)?.label ?? `${days}d`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-20 text-center text-fog">
        Failed to load analytics
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-display text-2xl font-bold tracking-display">Analytics</h1>
        <RangeToggle value={days} onChange={handleRangeChange} />
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          icon={<Play className="h-4 w-4" />}
          label="Total Plays"
          value={formatPlayCount(data.total_plays)}
          accent="spark"
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Today"
          value={formatPlayCount(data.plays_today)}
          trend={<TrendBadge current={data.plays_today} previous={data.plays_yesterday} />}
          accent="moss"
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="This Week"
          value={formatPlayCount(data.plays_this_week)}
          trend={<TrendBadge current={data.plays_this_week} previous={data.plays_last_week} />}
          accent="spark"
        />
        <StatCard
          icon={<Music className="h-4 w-4" />}
          label="Sounds"
          value={String(data.total_sounds)}
          sub={`~${data.avg_plays_per_sound} avg plays`}
          accent="spark"
        />
        <StatCard
          icon={<Users className="h-4 w-4" />}
          label={`Listeners (${rangeLabel})`}
          value={formatPlayCount(data.unique_sessions)}
          accent="spark"
        />
        <StatCard
          icon={<HardDrive className="h-4 w-4" />}
          label="Storage"
          value={formatBytes(data.total_storage_bytes)}
          accent="fog"
        />
      </div>

      <div className="mt-6 rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            Plays · Last {rangeLabel}
          </h2>
          <span className="font-mono text-[10px] text-fog">
            {data.plays_per_day.reduce((sum, d) => sum + d.count, 0)} total
          </span>
        </div>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.plays_per_day}>
              <defs>
                <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffb649" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#ffb649" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#252e47" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#8b95a8"
                fontSize={10}
                fontFamily="var(--font-mono)"
                tickFormatter={(d: string) => d.slice(5)}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#8b95a8"
                fontSize={10}
                fontFamily="var(--font-mono)"
                tickLine={false}
                axisLine={false}
                width={35}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#ffb649"
                strokeWidth={2}
                fill="url(#sparkGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
          <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            <Music className="h-3.5 w-3.5" /> Top sounds
          </h2>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.top_sounds.slice(0, 10).map((s) => ({
                  name: s.sound.title.length > 18 ? s.sound.title.slice(0, 18) + "…" : s.sound.title,
                  plays: s.play_count,
                }))}
                layout="vertical"
                margin={{ left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#252e47" horizontal={false} />
                <XAxis type="number" stroke="#8b95a8" fontSize={10} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke="#8b95a8" fontSize={10} fontFamily="var(--font-mono)" width={110} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="plays" fill="#ffb649" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
          <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            <Search className="h-3.5 w-3.5" /> Search queries
          </h2>
          <div className="mt-4 space-y-1.5">
            {data.top_search_queries.slice(0, 12).map((item, i) => {
              const maxCount = data.top_search_queries[0]?.count || 1;
              return (
                <div key={item.query} className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-slate-raised">
                  <span className="w-5 font-mono text-[10px] text-fog text-right">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-parchment">{item.query}</span>
                      <span className="shrink-0 font-mono text-[10px] text-fog">{item.count}</span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-mist">
                      <div className="h-full rounded-full bg-spark/60 transition-all" style={{ width: `${(item.count / maxCount) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {data.top_search_queries.length === 0 && (
              <p className="py-8 text-center text-sm text-fog">No searches yet</p>
            )}
          </div>
        </div>

      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {countries && (
          <div className="rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
            <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Globe className="h-3.5 w-3.5" /> Plays by country ({rangeLabel})
            </h2>
            <div className="mt-4 space-y-1.5">
              {countries.countries.slice(0, 12).map((item, i) => {
                const maxCount = countries.countries[0]?.count || 1;
                return (
                  <div key={item.country} className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-slate-raised">
                    <span className="w-5 font-mono text-[10px] text-fog text-right">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-parchment">
                          {COUNTRY_NAMES[item.country] ?? item.country}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-fog">{formatPlayCount(item.count)}</span>
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-mist">
                        <div className="h-full rounded-full bg-spark/60 transition-all" style={{ width: `${(item.count / maxCount) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {countries.countries.length === 0 && (
                <p className="py-8 text-center text-sm text-fog">No country data yet</p>
              )}
            </div>
          </div>
        )}

        {regions && (
          <div className="rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
            <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <MapPin className="h-3.5 w-3.5" /> Plays by state ({regions.country}, {rangeLabel})
            </h2>
            <div className="mt-4 space-y-1.5">
              {regions.regions.slice(0, 12).map((item, i) => {
                const maxCount = regions.regions[0]?.count || 1;
                return (
                  <div key={item.region} className="group flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-slate-raised">
                    <span className="w-5 font-mono text-[10px] text-fog text-right">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-parchment">{item.region}</span>
                        <span className="shrink-0 font-mono text-[10px] text-fog">{formatPlayCount(item.count)}</span>
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-mist">
                        <div className="h-full rounded-full bg-spark/60 transition-all" style={{ width: `${(item.count / maxCount) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {regions.regions.length === 0 && (
                <p className="py-8 text-center text-sm text-fog">No state data yet</p>
              )}
            </div>
          </div>
        )}

        {devices && (
          <div className="rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
            <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Smartphone className="h-3.5 w-3.5" /> Mobile vs Desktop ({rangeLabel})
            </h2>
            <div className="mt-4 flex items-center gap-6">
              <div className="h-48 w-48 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={devices.devices.map((d) => ({ name: d.device_type, value: d.count }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={4}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {devices.devices.map((_, i) => (
                        <Cell key={i} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {devices.devices.map((d, i) => {
                  const total = devices.devices.reduce((s, x) => s + x.count, 0);
                  const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                  return (
                    <div key={d.device_type} className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: DEVICE_COLORS[i % DEVICE_COLORS.length] + "20" }}>
                        {d.device_type === "mobile" ? <Smartphone className="h-4 w-4" style={{ color: DEVICE_COLORS[i] }} /> : <Monitor className="h-4 w-4" style={{ color: DEVICE_COLORS[i] }} />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm capitalize text-parchment">{d.device_type}</span>
                          <span className="font-mono text-[10px] text-fog">{pct}%</span>
                        </div>
                        <p className="font-mono text-[10px] text-fog">{formatPlayCount(d.count)} plays</p>
                      </div>
                    </div>
                  );
                })}
                {devices.devices.length === 0 && (
                  <p className="py-4 text-sm text-fog">No device data yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {heatmap && heatmap.heatmap.length > 0 && (
        <div className="mt-6 rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Clock className="h-3.5 w-3.5" /> Peak hours · Last {rangeLabel}
            </h2>
            <button
              onClick={toggleUtc}
              className="rounded-lg border border-mist px-2 py-0.5 font-mono text-[10px] text-fog transition-colors hover:border-spark/50 hover:text-parchment"
            >
              {timezoneLabel(tz)}
            </button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <HeatmapGrid
              data={shiftHeatmap(heatmap.heatmap, getTimezoneOffsetHours(tz))}
              tzLabel={timezoneLabel(tz)}
            />
          </div>
        </div>
      )}

      {listeners && listeners.daily_listeners.length > 0 && (
        <div className="mt-6 rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              <Users className="h-3.5 w-3.5" /> Unique daily listeners · {rangeLabel}
            </h2>
            <span className="font-mono text-[10px] text-fog">
              {Math.max(...listeners.daily_listeners.map((d) => d.listeners))} peak
            </span>
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={listeners.daily_listeners}>
                <defs>
                  <linearGradient id="listenersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b95a8" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8b95a8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#252e47" vertical={false} />
                <XAxis dataKey="date" stroke="#8b95a8" fontSize={10} fontFamily="var(--font-mono)" tickFormatter={(d: string) => d.slice(5)} tickLine={false} axisLine={false} />
                <YAxis stroke="#8b95a8" fontSize={10} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} width={35} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="listeners" stroke="#8b95a8" strokeWidth={2} fill="url(#listenersGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {featured && featured.history.length > 0 && (
        <div className="mt-6 rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
          <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            <Star className="h-3.5 w-3.5" /> Featured pick history
          </h2>
          <div className="mt-4 space-y-2">
            {featured.history.map((day) => (
              <div key={day.date} className="flex flex-col gap-1.5 rounded-xl bg-slate-raised px-3 py-2.5 sm:flex-row sm:items-center sm:gap-4">
                <span className="shrink-0 font-mono text-[10px] text-fog">{day.date}</span>
                <div className="flex flex-1 flex-wrap gap-2">
                  {day.picks.map((pick) => (
                    <span key={pick.pick_type} className="flex items-center gap-1.5 rounded-lg bg-mist/50 px-2.5 py-1">
                      {PICK_ICONS[pick.pick_type]}
                      <span className="text-xs text-parchment">{pick.title}</span>
                      <span className="font-mono text-[9px] text-fog">{formatPlayCount(pick.play_count)}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Usage */}
      {aiStats && (
        <div className="mt-6">
          <h2 className="flex items-center gap-2 font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
            <Sparkles className="h-3.5 w-3.5" /> AI Usage
          </h2>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<Wand2 className="h-4 w-4" />}
              label={`Requests (${rangeLabel})`}
              value={String(aiStats.total_requests)}
              accent="spark"
            />
            <StatCard
              icon={<Sparkles className="h-4 w-4" />}
              label={`Neurons (${rangeLabel})`}
              value={formatPlayCount(aiStats.total_neurons)}
              accent="spark"
            />
            <StatCard
              icon={<Users className="h-4 w-4" />}
              label="Public Today"
              value={`${aiStats.today.public.request_count} req`}
              sub={`${formatPlayCount(aiStats.today.public.neuron_estimate)} / ${formatPlayCount(aiStats.today.public.cap)} neurons`}
              accent={aiStats.today.public.neuron_estimate > aiStats.today.public.cap * 0.8 ? "ember" : "moss"}
            />
            <StatCard
              icon={<Monitor className="h-4 w-4" />}
              label="Admin Today"
              value={`${aiStats.today.admin.request_count} req`}
              sub={`${formatPlayCount(aiStats.today.admin.neuron_estimate)} / ${formatPlayCount(aiStats.today.admin.cap)} neurons`}
              accent={aiStats.today.admin.neuron_estimate > aiStats.today.admin.cap * 0.8 ? "ember" : "moss"}
            />
          </div>

          {aiStats.per_day.length > 0 && (
            <div className="mt-4 rounded-[18px] border border-mist bg-slate p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                  AI Requests · Last {rangeLabel}
                </h3>
                <span className="font-mono text-[10px] text-fog">
                  {aiStats.total_requests} total
                </span>
              </div>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aiStats.per_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#252e47" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#8b95a8"
                      fontSize={10}
                      fontFamily="var(--font-mono)"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(d: string) => d.slice(5)}
                    />
                    <YAxis stroke="#8b95a8" fontSize={10} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="public_requests" name="Public" stackId="a" fill="#ffb649" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="admin_requests" name="Admin" stackId="a" fill="#8b95a8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeatmapGrid({ data, tzLabel = "UTC" }: { data: Array<{ hour: number; day: number; count: number }>; tzLabel?: string }) {
  const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
  let max = 0;
  for (const d of data) {
    if (d.day >= 0 && d.day < 7 && d.hour >= 0 && d.hour < 24) {
      grid[d.day][d.hour] = d.count;
      if (d.count > max) max = d.count;
    }
  }

  return (
    <div className="min-w-[600px]">
      <div className="flex gap-0.5">
        <div className="w-10 shrink-0" />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="flex-1 text-center font-mono text-[8px] text-fog">
            {h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`}
          </div>
        ))}
      </div>
      {grid.map((row, day) => (
        <div key={day} className="flex items-center gap-0.5">
          <span className="w-10 shrink-0 text-right font-mono text-[9px] text-fog pr-1.5">{DAY_LABELS[day]}</span>
          {row.map((count, hour) => {
            const intensity = max > 0 ? count / max : 0;
            return (
              <div
                key={hour}
                className="group relative flex-1"
                title={`${DAY_LABELS[day]} ${hour}:00 ${tzLabel} — ${count} plays`}
              >
                <div
                  className="aspect-square rounded-[3px]"
                  style={{ backgroundColor: intensity === 0 ? "#252e47" : `rgba(255, 182, 73, ${0.15 + intensity * 0.85})` }}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  trend,
  sub,
  accent = "spark",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  trend?: React.ReactNode;
  sub?: string;
  accent?: "spark" | "moss" | "ember" | "fog";
}) {
  const accentColors = {
    spark: "border-spark/20 bg-spark/5",
    moss: "border-moss/20 bg-moss/5",
    ember: "border-ember/20 bg-ember/5",
    fog: "border-mist bg-slate",
  };

  return (
    <div className={cn("rounded-[18px] border p-4", accentColors[accent])}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-fog">
          {icon}
          <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
        </div>
        {trend}
      </div>
      <p className="mt-2 font-display text-2xl font-bold tracking-display text-parchment">{value}</p>
      {sub && <p className="mt-0.5 font-mono text-[10px] text-fog">{sub}</p>}
    </div>
  );
}
